import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import CloseGray from '../../../../assets/icons_new/CloseGray.svg'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { FaIcon, faEnvelope, faTrash } from '@/lib/fa'
import {
  useMutationSendInvitation,
  useQueryAdminUsers,
  UserListRequestPayload,
} from '@/lib/api/company/configuration/adminPanel/users'
import { removeAdminUser } from '@/lib/api/company/configuration/adminPanel/users/removeAdminUser'
import { QUERY_KEY_SETTINGS_ADMIN_PANEL } from '@/lib/api/constants'
import { toast } from '@/lib/toasts/api'
import { useHasAccess } from '@/modules/shared/permissions'

interface UsersSelectedActionBarProps {
  companyId: string
  selectedGroup: SelectionGroup
  resetAll: () => void
}

const UsersSelectedActionBar: React.FC<UsersSelectedActionBarProps> = ({
  companyId,
  selectedGroup,
  resetAll,
}) => {
  const { t } = useLocalize()
  const wrapConfirm = useWrapConfirm()
  const queryClient = useQueryClient()

  const [isDeleting, setIsDeleting] = React.useState(false)
  const { allowed: canRemoveUser } = useHasAccess({ permissions: 'canRemoveUser' }, companyId)
  const mutationSendInvitation = useMutationSendInvitation(companyId)

  // Get users data to find logins for selected IDs
  const [filters] = React.useState<UserListRequestPayload>({
    take: 1000, // Get enough users to find selected ones
    skip: 0,
  })
  const usersQuery = useQueryAdminUsers({ companyId, payload: filters })
  const users = usersQuery.data?.users || []

  const selectedCount = selectedGroup?.ids?.length || 0

  const handleDelete = async () => {
    if (!selectedGroup?.ids?.length || isDeleting) return

    setIsDeleting(true)
    try {
      await Promise.all(
        selectedGroup.ids.map((id) =>
          removeAdminUser({
            companyId,
            userIdToRemove: String(id),
          }),
        ),
      )
      await queryClient.invalidateQueries({ queryKey: [
        QUERY_KEY_SETTINGS_ADMIN_PANEL,
        'users',
        companyId,
      ] })
      resetAll()
      toast.success({ text: t('usersModule.usersDeleted') })
    } catch (error) {
      console.error('Error deleting users:', error)
      toast.error({ text: t('usersModule.deleteError') })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSendInvitation = async () => {
    if (!selectedGroup?.ids || selectedGroup.ids.length === 0) {
      return
    }

    // Find selected users by their IDs
    const selectedUsers = users.filter((user) =>
      selectedGroup.ids.some((id) => id === user.id)
    )

    if (selectedUsers.length === 0) {
      console.warn('No users found for selected IDs')
      return
    }

    // Send invitation for each selected user
    try {
      await Promise.all(
        selectedUsers.map((user) =>
          mutationSendInvitation.mutateAsync(user.login)
        )
      )
      resetAll()
      toast.success({ text: t('usersModule.invitationSent') })
    } catch (error) {
      console.error('Error sending invitations:', error)
      toast.error({ text: t('usersModule.invitationError') })
    }
  }

  const isLoading = mutationSendInvitation.isPending

  return (
    <div className="tw-relative tw-z-20 tw-flex tw-w-full tw-justify-between tw-border-b-2 tw-border-primary tw-bg-[#F1F4FF] tw-px-4 tw-py-3.5 lg:tw-items-center">
      <div className="tw-absolute -tw-bottom-1.5 tw-left-3">
        <div className="tw-absolute -tw-top-1.5 !tw-z-10 tw-h-2 tw-w-2 tw-bg-[#F1F4FF]"></div>
        <div className="tw-h-2 tw-w-2 tw-rotate-45 tw-bg-primary"></div>
      </div>

      <div className="tw-flex tw-flex-1 tw-items-center tw-gap-5">
        <p className="tw-space-x-2 tw-text-sm">
          <span className="tw-inline-block">{t('common.selected')}</span>
          <span className="tw-font-medium">{selectedCount}</span>
        </p>

        <div className="tw-flex tw-items-center tw-gap-4">
          <Tooltip
            text={
              isLoading
                ? t('usersModule.sending')
                : t('usersModule.resendInvitation')
            }
            onClick={isLoading ? undefined : handleSendInvitation}
          >
            <div
              className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 ${
                isLoading
                  ? 'tw-cursor-not-allowed tw-opacity-50'
                  : 'tw-cursor-pointer hover:tw-bg-[#e5e6e6]'
              }`}
            >
              <FaIcon
                icon={faEnvelope}
                className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
              />
            </div>
          </Tooltip>

          {canRemoveUser && (
            <Tooltip
              text={isDeleting ? t('usersModule.deleting') : t('common.delete')}
              onClick={
                isDeleting
                  ? undefined
                  : wrapConfirm(handleDelete, {
                      message: t('usersModule.confirmDeleteUsers'),
                    })
              }
            >
              <div
                className={`tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 ${
                  isDeleting
                    ? 'tw-cursor-not-allowed tw-opacity-50'
                    : 'tw-cursor-pointer hover:tw-bg-[#e5e6e6]'
                }`}
              >
                <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-red-500" />
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <Tooltip text={t('common.close')} onClick={() => resetAll()}>
        <div className="tw-relative tw-cursor-pointer">
          <div className="tw-absolute -tw-top-1.5 tw-left-1/2 tw-z-0 tw-h-7 tw-w-7 tw--translate-x-1/2 tw-rounded-full tw-bg-white tw-p-2 tw-shadow lg:tw-top-1/2 lg:tw--translate-y-1/2"></div>
          <img className="tw-z-1 tw-relative" src={CloseGray} alt="Close Icon" />
        </div>
      </Tooltip>
    </div>
  )
}

export default UsersSelectedActionBar
